import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { FormProvider as Form } from "react-hook-form";
import { useApp } from "../../context/AppState";
import { getCartDrawerWidgetBounds } from "../../utils/cartDrawerRuntime";
import {
  isExtensionEditorSurfacePreview,
  resolveExtensionSurface,
} from "../../../shared/extension-preview.js";

export function FormProvider({
  children,
  onSubmit,
  methods,
  popupData,
  popupState,
  isCartDrawerWidget = false,
  cartDrawerWidgetBounds,
  inline = false,
}) {
  const { isMobile, appState } = useApp();
  const activeStep = useMemo(() => popupState?.activeStep, [popupState]);

  const [maxDimensions, setMaxDimensions] = useState({ width: 0, height: 0 });
  const resolvedCartDrawerBounds = useMemo(
    () =>
      isCartDrawerWidget
        ? cartDrawerWidgetBounds || getCartDrawerWidgetBounds(popupData)
        : null,
    [cartDrawerWidgetBounds, isCartDrawerWidget, popupData]
  );

  const embeddingData = useMemo(
    () => popupData?.generalSettings?.condition?.embed,
    [popupData]
  );

  const extensionSurface = useMemo(
    () => resolveExtensionSurface(popupData, appState?.extensionSurface),
    [popupData, appState?.extensionSurface]
  );

  const useExtensionSurfaceFill =
    appState?.is_preview_mode &&
    isExtensionEditorSurfacePreview(extensionSurface);

  const width =
    popupData?.schema[activeStep]?.popupSettings?.customCSS?.[
      isMobile ? "mobile" : "desktop"
    ]?.width ?? 0;
  const height =
    popupData?.schema[activeStep]?.popupSettings?.customCSS?.[
      isMobile ? "mobile" : "desktop"
    ]?.height ?? 0;

  useEffect(() => {
    if (useExtensionSurfaceFill) return;

    if (
      embeddingData?.is_embedded &&
      (popupState?.hasOwnProperty("isEmbeded") ? activeStep > 0 ? false : popupState.isEmbeded : true)
    ) {
      if (height > 0 || width > 0) {
        return setMaxDimensions({
          width,
          height,
        });
      }
      // Get dimensions of all nested children
      const childDimensions = popupData?.schema[activeStep]?.sections?.map(
        (section) => ({
          width:
            section?.setting?.dimension?.[isMobile ? "mobile" : "desktop"]
              ?.width ?? 0,
          height:
            section?.setting?.dimension?.[isMobile ? "mobile" : "desktop"]
              ?.height ?? 0,
        })
      );

      // Calculate maximum width and height
      let maxWidth = 0;
      let maxHeight = 0;

      if (childDimensions) {
        maxWidth = Math.max(...childDimensions?.map((dim) => dim.width));
        maxHeight = Math.max(...childDimensions?.map((dim) => dim.height));
      }

      // Set the maximum dimensions
      setMaxDimensions({
        width: maxWidth,
        height: maxHeight,
      });
    }
  }, [
    popupData,
    width,
    height,
    activeStep,
    isMobile,
    embeddingData,
    children,
    popupData?.schema[activeStep]?.sections,
    useExtensionSurfaceFill,
  ]);

  const formStyle = inline
    ? {
        width: "100%",
        height: "auto",
        boxSizing: "border-box",
        position: "relative",
      }
    : useExtensionSurfaceFill
    ? {
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        height: "100%",
        minHeight: "100%",
        flex: "1 1 auto",
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }
    : {
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        height:
          resolvedCartDrawerBounds?.displayHeight ||
          resolvedCartDrawerBounds?.height ||
          maxDimensions.height ||
          "auto",
        minHeight:
          resolvedCartDrawerBounds?.displayHeight ||
          resolvedCartDrawerBounds?.height ||
          undefined,
        overflow: isCartDrawerWidget ? "visible" : undefined,
        position: "relative",
      };

  return (
    <Form {...methods}>
      <form
        onSubmit={(event) => {
          if (appState?.is_preview_mode) {
            event.preventDefault();
            return;
          }
          onSubmit(event);
        }}
        noValidate
        style={formStyle}
      >
        {children}
      </form>
    </Form>
  );
}

FormProvider.propTypes = {
  children: PropTypes.node,
  methods: PropTypes.object,
  onSubmit: PropTypes.func,
  popupData: PropTypes.object,
  popupState: PropTypes.object,
  isCartDrawerWidget: PropTypes.bool,
  cartDrawerWidgetBounds: PropTypes.object,
  inline: PropTypes.bool,
};
